<template>
  <v-card class="rounded-xl pa-8" outlined>

    <!-- Step Content -->
    <div class="overflow-y-auto d-flex" style="height: 560px;">
          <component ref="stepComponentRef" class="pt-4" :is="currentStepComponent" />
    </div>

    <!-- Progress Bar -->
    <v-row class="mt-12">
      <v-col cols="12" style="position:relative;"> 
        <v-row justify="space-between" align="center" no-gutters>
          <v-col
            v-for="n in 6"
            :key="n"
            cols="2"
            class="progress-segment-col"
            style="position:relative;"
          >
            <div
              v-if="n === wizardStore.currentStep"
              class="font-weight-medium"
              style="font-size:1.1rem; position:absolute; top:-28px; left:50%; transform:translateX(-50%); white-space:nowrap; z-index:1;"
            >
              {{ $t('rollOffWizard.nav.stepOf', { n: n, total: 6 }) }}
            </div>
            <v-progress-linear
              v-if="wizardStore.currentStep < 9"
              :value="n <= wizardStore.currentStep ? 100 : 0"
              height="8"
              color="transparent"
              background-color="grey lighten-2"
              :style="n <= wizardStore.currentStep ? { backgroundImage: stepColors[n - 1] } : {}"
              rounded
            ></v-progress-linear>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Navigation Buttons -->
    <v-row justify="space-between">
      <v-col cols="auto">
        <v-btn v-if="currentButtonConfig.showBack"
          class="semi-bold"
          color="secondary"
          text
          @click="wizardStore.prevView"
          style="text-transform: none"
          :disabled="indexStore.loading"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <span v-else-if="currentButtonConfig.showOther" class="wizard-link-group">
          <a
            href="#"
            class="wizard-link"
            @click.prevent="wizardStore.setView('WizardProjectType')"
            :style="{ pointerEvents: indexStore.loading ? 'none' : 'auto', opacity: indexStore.loading ? 0.5 : 1 }"
          >{{ $t('rollOffWizard.nav.startOver') }}</a>
          <span class="wizard-link-sep">{{ $t('rollOffWizard.nav.or') }}</span>
          <a
            href="#"
            class="wizard-link"
            @click.prevent="wizardStore.toggleShowAllOptions()"
            :style="{ pointerEvents: indexStore.loading ? 'none' : 'auto', opacity: indexStore.loading ? 0.5 : 1 }"
          >{{ wizardStore.showAllOptions ? $t('rollOffWizard.nav.showRecommendedOption') : $t('rollOffWizard.nav.seeOtherOptions') }}</a>
        </span>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="currentButtonConfig.showContinue"
          color="success"
          class="rounded-lg semi-bold"
          @click="onContinue"
          :loading="indexStore.loading"
          :disabled="indexStore.loading"
        >
          {{ (wizardStore.currentStep === 4 && wizardStore.selectedDisposal?.length < 1) ? $t('rollOffWizard.nav.skip') : $t(currentButtonConfig.continueText) }}
        </v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="userStore.recaptchaDialog" attach hide-overlay max-width="290">
      <v-card>
          <v-card-title class="text-h5 d-flex justify-center">
              <v-icon x-large color="primary">mdi-alert-circle-outline</v-icon>
          </v-card-title>
          <v-card-text class="text-center">{{ $t('snackbar.body1') }}</v-card-text>
          <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="userStore.recaptchaDialog = false">
                  {{ $t('snackbar.close') }}
              </v-btn>
          </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { computed, ref, watch, provide, onMounted, defineProps } from 'vue'
import googleApi from '../../plugins/google';
import salesforce from '../../plugins/salesforce';
import i18n from "@/i18n";

import { useRollOffWizardStore } from '@/store/rollOffWizard'
import { useIndexStore } from '@/store/index'
import { useUserStore } from '@/store/user'
import { useAxiosFormStore } from "@/store/axios"

import WizardIntro from './WizardIntro.vue'
import WizardContact from './WizardContact.vue'
import WizardProjectType from './WizardProjectType.vue'
import WizardPickupType from './WizardPickupType.vue'
import WizardDisposal from './WizardDisposal.vue'
import WizardMap from './WizardMap.vue'
import WizardNotes from './WizardNotes.vue'
import WizardLoading from './WizardLoading.vue'
import WizardResult from './WizardResult.vue'
import WizardConfirmation from './WizardConfirmation.vue'

const props = defineProps({
  lang: String,
})

// Variables
const stepComponentRef = ref(null)
const stepMap = {
  WizardIntro,
  WizardContact,
  WizardProjectType,
  WizardPickupType,
  WizardDisposal,
  WizardMap,
  WizardNotes,
  WizardLoading,
  WizardResult,
  WizardConfirmation
}

const stepColors = [
  'linear-gradient(to right, #374fdb, #3d6cda)',
  'linear-gradient(to right, #3d6cda, #3a8dd0)',
  'linear-gradient(to right, #3a8dd0, #35acc2)',
  'linear-gradient(to right, #35acc2, #2fc0b1)',
  'linear-gradient(to right, #2fc0b1, #26d1a2)',
  'linear-gradient(to right, #26d1a2, #1dc795)' 
];

// Importing stores
const wizardStore = useRollOffWizardStore()
const indexStore = useIndexStore()
const userStore = useUserStore()
const axiosFormStore = useAxiosFormStore()

// Assign language to i18n and the wizard store
i18n.locale = props.lang;
wizardStore.lang = props.lang;

// Step button configuration
const stepButtonConfig = [
  // step 0 - Intro
  { showBack: false, showContinue: true, showOther: false, continueText: "rollOffWizard.nav.getStarted" },
  // step 1 - Contact
  { showBack: true, showContinue: true, showOther: false, continueText: "continue" },
  // step 2 - Project Type
  { showBack: true, showContinue: true, showOther: false, continueText: "rollOffWizard.nav.skip" },
  // step 3 - Pickup Type
  { showBack: true, showContinue: true, showOther: false, continueText: "rollOffWizard.nav.skip" },
  // step 4 - Disposal
  { showBack: true, showContinue: true, showOther: false, continueText: "continue" },
  // step 5 - Map
  { showBack: true, showContinue: true, showOther: false, continueText: "continue" },
  // step 6 - Notes
  { showBack: true, showContinue: true, showOther: false, continueText: "rollOffWizard.nav.showBestOption" },
  // step 7 - Loading
  { showBack: false, showContinue: false, showOther: false, continueText: "" },
  // step 8 - Result
  { showBack: false, showContinue: true, showOther: true, continueText: "rollOffWizard.nav.requestServiceProposal" },
  // step 9 - Confirmation 
  { showBack: false, showContinue: true, showOther: false, continueText: "rollOffWizard.nav.exploreMoreSolutions" }
]

// Computed property to determine the current step component
const currentStepComponent = computed(() => stepMap[wizardStore.currentView] || WizardIntro)
const currentButtonConfig = computed(() => stepButtonConfig[wizardStore.currentStep] || { showBack: false, showContinue: false, showOther: false, continueText: "" })

// Step handlers for better organization
const stepHandlers = {
  0: handleIntroStep,
  1: handleContactStep,
  2: handleProjectTypeStep,
  3: handlePickupTypeStep,
  4: handleDisposalStep,
  5: handleMapStep,
  6: handleNotesStep,
  8: handleResultStep,
  9: handleConfirmationStep
}

// Function to handle the intro step
function handleIntroStep() {
  if (!stepComponentRef.value?.validateIntroForm?.()) return false;
  
  indexStore.fbEventId = indexStore.generateUniqueFbEventId();
  indexStore.loading = true;
  indexStore.reCaptchaValidation('validationQuote');

  // Watch for loading to complete - this handles the case when no dialog is shown
  const unwatch = watch(
    () => indexStore.loading,
    (val) => {
      if (!val) {
        unwatch();
        // Only advance if we're still on step 0 and no dialog is showing
        if (wizardStore.currentStep === 0 && !indexStore.dialog) {
          wizardStore.nextView();
        }
      }
    }
  );
  return false; // Prevent immediate nextView
}

// Function to handle the contact step
function handleContactStep() {
  if (!stepComponentRef.value?.validateContactForm?.()) return false;

  // Accept conditions here because there is no step for it
  userStore.form.acceptConditions = true;
  
  const leadType = indexStore.getLeadTypeFromServiceType(
    userStore.form.serviceType, 
    userStore.form.categoryType
  );
  indexStore.sortServicePackges(leadType);

  // Send HTTP request for lead information 
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());

  indexStore.loading = false;
  return true;
}

// Function to handle the project type step
function handleProjectTypeStep() {
  userStore.form.webLeadStep = 'Project Stage';
  
  // Send HTTP request for lead information
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());

  indexStore.loading = false;
  return true;
}

// Function to handle the pickup type step
function handlePickupTypeStep() {
  userStore.form.webLeadStep = 'Pickup Stage';
  
  // Send HTTP request for lead information
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());

  indexStore.loading = false;  
  return true;
}

// Function to handle the disposal step
function handleDisposalStep() {
  userStore.form.webLeadStep = 'Disposal Stage';

  // Send HTTP request for lead information
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());

  if (wizardStore.selectedDisposal?.length >= 2) {
    wizardStore.showWarning = true;
    return false;
  }

  indexStore.loading = false;  
  return true;
}

// Function to handle the map step
function handleMapStep() {  
  userStore.form.webLeadStep = 'Map Stage';
  
  // Send HTTP request for lead information
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());
  
  indexStore.loading = false;
  return true;
}

// Function to handle the notes step
function handleNotesStep() {
  userStore.form.webLeadStep = 'Notes Stage';
  
  // Send HTTP request for lead information
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());
  
  indexStore.loading = false;
  return true;
}

// Function to handle the result step
function handleResultStep() {
  indexStore.loading = true;
  
  // Build description from wizard data
  userStore.form.description = buildWizardDescription();
  userStore.form.webLeadStep = 'Result Stage';
  userStore.form.categoryType = 'Dumpster Rental (Remodels, Construction, Etc.)';
  
  // Set services based on selected product
  const equivalent = wizardStore.findRollOffEquivalent(wizardStore.selectedProduct);
  userStore.form.servicesWantedJSON = equivalent ? [equivalent] : [];
  
  indexStore.sendHttpRequestQuote('LeadInformation', axiosFormStore.getDataToAxios());

  // Wait 2 seconds before moving to the next view
  setTimeout(() => {
    indexStore.loading = false;
    wizardStore.nextView();
  }, 2000);
  
  return false; // Prevent immediate nextView
}

// Function to handle the confirmation step
function handleConfirmationStep() {
  wizardStore.resetWizard();
  indexStore.resetAll();
  return false; // Prevent immediate nextView
}

// Function to build the wizard description from the store data
function buildWizardDescription() {
  return `
    Project Type: ${wizardStore.projectType || ''}
    Pickup Type: ${wizardStore.pickupType || ''}
    Disposal: ${wizardStore.selectedDisposal || ''}
    Map Location: ${wizardStore.mapLocation ? JSON.stringify(wizardStore.mapLocation) : ''}
    Placement: ${wizardStore.placement || ''}
    Accessibility: ${wizardStore.accessibility || ''}
    Space: ${wizardStore.space || ''}
    Details: ${wizardStore.details || ''}
    Selected Product: ${wizardStore.selectedProduct ? wizardStore.selectedProduct.title : ''}
  `.trim();
}

// Function to handle the "Continue" button click
function onContinue() {
  const currentStep = wizardStore.currentStep;
  const handler = stepHandlers[currentStep];
  
  if (handler) {
    const shouldContinue = handler();
    if (!shouldContinue) return;
  }
  
  wizardStore.nextView();
}

provide('onContinue', onContinue)

function checkIfGTMLoadedThenSendLoadForm() {
  if (window.parent.google_tag_manager && typeof window.parent.dataLayer) {
    googleApi.gTagSendEvents(window.parent.location.href, 0, "Load Form", process.env.VUE_APP_GTAG_LEAD_CATEGORY);
  }else {
    setTimeout(checkIfGTMLoadedThenSendLoadForm, 500);
  }
}

watch(
  () => indexStore.dialog,
  (val, oldVal) => {
    if (oldVal === true && val === false && wizardStore.currentStep === 0) {
      // Always reset loading
      indexStore.loading = false;
      // Only advance if the address is valid AND user didn't click back
      if (stepComponentRef.value?.validateIntroForm?.() && !indexStore.dialogCancelled) {
        wizardStore.nextView();
      }
      // Reset the cancellation flag after processing
      indexStore.dialogCancelled = false;
    }
  }
);

onMounted(() => {
  checkIfGTMLoadedThenSendLoadForm();
  salesforce.campaignStorageTracking();
});
</script>

<style scoped>
.progress-segment-col {
  padding: 0px 4px !important;
}
.progress-segment-col:first-child {
  padding-left: 0 !important;
}
.progress-segment-col:last-child {
  padding-right: 0 !important;
}
.wizard-link-group {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
}
.wizard-link {
  color: #16597a;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s;
}
.wizard-link:hover {
  color: #0d3c53;
  text-decoration: underline;
}
.wizard-link-sep {
  color: #444;
  font-weight: 400;
  margin: 0 2px;
}
::v-deep .semi-bold {
  font-weight: 600;
}
::v-deep .border-2-point-5 {
  border-width:2.5px !important;
}
</style>
